import {
  isMeetingAnnouncement,
  parseMeetingAnnouncement,
  serializeMeetingAnnouncement,
  withComputedMeeting,
  toIsoDate,
  padTime,
  combineIstIso,
  displayNameFromEmail,
} from "../utils/meetings";

const DEPLOYED_API_URL =
  "https://z0nrgtv865.execute-api.ap-south-1.amazonaws.com/prod";

export const LOCAL_API_REQUIRED_MESSAGE =
  "API base URL is not configured. Local development uses the deployed AWS API unless REACT_APP_USE_LOCAL_API=true and REACT_APP_API_URL is a loopback address such as http://127.0.0.1:3001.";

export function isLoopbackApiUrl(raw) {
  try {
    const host = new URL(String(raw || "").trim()).hostname.toLowerCase();
    return host === "localhost" || host === "127.0.0.1" || host === "[::1]";
  } catch {
    return false;
  }
}

function flagEnabled(value) {
  return String(value || "").trim().toLowerCase() === "true";
}

/**
 * CRA `npm start` uses the deployed AWS API: REACT_APP_API_URL when it is
 * not a loopback address, otherwise the known API Gateway URL.
 * A local backend is used only when REACT_APP_USE_LOCAL_API=true and
 * REACT_APP_API_URL is a loopback address. An empty base is never returned
 * for normal local development, so requests cannot fall through to the
 * frontend origin.
 */
export function getApiBaseUrl(
  raw = process.env.REACT_APP_API_URL,
  options = {}
) {
  const useLocalApi =
    options.useLocalApi ?? flagEnabled(process.env.REACT_APP_USE_LOCAL_API);
  const fromEnv = String(raw || "")
    .trim()
    .replace(/\/+$/, "");
  const loopback = Boolean(fromEnv) && isLoopbackApiUrl(fromEnv);

  if (useLocalApi) {
    return loopback ? fromEnv : "";
  }

  if (!fromEnv || loopback) return DEPLOYED_API_URL;
  return fromEnv;
}

const API = getApiBaseUrl();

function parseJwtPayload(token) {
  try {
    const payload = token.split(".")[1];
    return JSON.parse(atob(payload.replace(/-/g, "+").replace(/_/g, "/")));
  } catch {
    return null;
  }
}

/** Returns true if JWT is missing or past exp (with 30s skew). */
export function isTokenExpired(token = localStorage.getItem("token")) {
  if (!token) return true;
  const payload = parseJwtPayload(token);
  if (!payload?.exp) return false;
  return Date.now() >= payload.exp * 1000 - 30_000;
}

function clearSessionAndRedirectToLogin() {
  try {
    localStorage.removeItem("token");
    localStorage.removeItem("role");
    localStorage.removeItem("actualRole");
  } catch {
    /* ignore */
  }
  if (!window.location.pathname.startsWith("/login")) {
    window.location.replace("/login");
  }
}

async function errorFromResponse(res, fallback = "API request failed") {
  const text = await res.text();
  let message = fallback;
  let code;
  let activeTasks;
  let attendance;
  try {
    const data = JSON.parse(text);
    if (typeof data === "string" && data.trim()) message = data;
    else if (data?.error) message = data.error;
    else if (data?.message) message = data.message;
    if (data && typeof data === "object" && data.code) code = data.code;
    if (Array.isArray(data?.activeTasks)) activeTasks = data.activeTasks;
    if (data && typeof data === "object" && data.attendance) {
      attendance = data.attendance;
    }
  } catch {
    if (text?.trim()) message = text.trim();
  }
  const err = new Error(sanitizeApiErrorMessage(message, res.status, fallback));
  err.status = res.status;
  if (code) err.code = code;
  if (activeTasks) err.activeTasks = activeTasks;
  if (attendance) err.attendance = attendance;
  return err;
}

function fallbackForStatus(status) {
  if (status === 401) return "Authentication required. Please sign in again.";
  if (status === 403) {
    return "You do not have permission to access this resource.";
  }
  if (status === 404) {
    return "This API endpoint is not available on the deployed backend.";
  }
  if (status === 413) return "This file is too large to send through the server.";
  if (status >= 500) {
    return "The deployed backend returned a server error. Please try again.";
  }
  return "API request failed";
}

function sanitizeApiErrorMessage(message, status, fallback) {
  const text = String(message || "").trim();
  if (!text || /local api/i.test(text)) {
    return fallback || fallbackForStatus(status);
  }
  return text;
}

function networkApiError(networkErr) {
  const err = new Error(
    "Unable to reach the deployed AWS API. Check your connection and try again."
  );
  err.cause = networkErr;
  err.isNetworkError = true;
  return err;
}

export const api = async (path, method = "GET", body) => {
  if (!API) {
    throw new Error(LOCAL_API_REQUIRED_MESSAGE);
  }
  const token = localStorage.getItem("token");
  const headers = { "Content-Type": "application/json" };
  if (token) headers.Authorization = `Bearer ${token}`;

  // Session expired — stop calling APIs and send user to login
  if (token && isTokenExpired(token)) {
    clearSessionAndRedirectToLogin();
    throw new Error("Session expired. Please sign in again.");
  }

  let res;
  try {
    res = await fetch(`${API}${path}`, {
      method,
      headers,
      body:
        body !== undefined
          ? JSON.stringify(body)
          : method === "POST" || method === "PUT"
            ? "{}"
            : null,
    });
  } catch (networkErr) {
    const err = networkApiError(networkErr);
    console.warn("API network error:", path, networkErr);
    throw err;
  }

  if (res.status === 401) {
    clearSessionAndRedirectToLogin();
    throw new Error("Session expired. Please sign in again.");
  }

  if (!res.ok) {
    const err = await errorFromResponse(
      res,
      fallbackForStatus(res.status)
    );
    console.error("API error:", res.status, err.message);
    throw err;
  }

  return res.json();
};

/** Same as api(), but 401/403 do not clear the session (for optional fallbacks). */
export const apiOptional = async (path, method = "GET", body) => {
  const token = localStorage.getItem("token");
  const headers = { "Content-Type": "application/json" };
  if (token) headers.Authorization = `Bearer ${token}`;

  if (token && isTokenExpired(token)) {
    throw new Error("Session expired. Please sign in again.");
  }

  let res;
  try {
    res = await fetch(`${API}${path}`, {
      method,
      headers,
      body:
        body !== undefined
          ? JSON.stringify(body)
          : method === "POST" || method === "PUT"
            ? "{}"
            : null,
    });
  } catch (networkErr) {
    throw networkApiError(networkErr);
  }

  if (!res.ok) {
    throw await errorFromResponse(res, fallbackForStatus(res.status));
  }

  return res.json();
};

/* ================= SKILLS & TRAINING ================= */

export const fetchSkills = () => api("/skills", "GET");
export const fetchUserTrainings = () => api("/getUserTrainingList", "POST", {});
export const fetchTrainingVideoUrl = (video_s3_key) =>
  api("/getTrainingVideoUrl", "POST", { video_s3_key });
export const getTrainingUploadUrl = (fileName, skill) =>
  api("/getTrainingUploadUrl", "POST", { fileName, skill });
export const addTrainingMaterial = (data) =>
  api("/addTrainingMaterial", "POST", data);

/* ================= USERS (ADMIN) ================= */

export const fetchUsers = () => api("/admin/users", "GET");
export const fetchUserProfile = (email) =>
  api(
    email
      ? `/admin/getUserProfile?email=${encodeURIComponent(email)}`
      : "/admin/getUserProfile",
    "GET"
  );
export const saveUserProfile = (payload) =>
  api("/admin/save-user-profile", "POST", payload);
export const updateUserRole = (email, role) =>
  api("/admin/users", "POST", { email, action: "changeRole", role });
export const updateUserStatus = (email, action) =>
  api("/admin/users", "POST", { email, action });

export const resetUserPassword = (email) =>
  api("/admin/users", "POST", { email, action: "resetPassword" });

export const deleteUser = (email) =>
  api("/admin/users", "POST", { email, action: "delete" });

/* ================= PROFILE IMAGE ================= */

export const getProfileImageUploadUrl = (file, email) =>
  api("/getProfileImageUploadUrl", "POST", {
    fileName: file.name,
    contentType: file.type,
    email,
  });

/* ================= ACTIVITY ================= */

export const logActivity = (payload) => api("/activity", "POST", payload);
export const fetchMyActivityToday = (date) =>
  api(`/activity/today${date ? `?date=${date}` : ""}`, "GET");
/** Composed employee My Activity for one company date. Never send email. */
export const fetchMyDayActivity = (date) => {
  const qs = new URLSearchParams();
  if (date) qs.set("date", date);
  const query = qs.toString();
  return api(`/me/activity${query ? `?${query}` : ""}`, "GET");
};
export const fetchAdminActivity = (date, email) => {
  const qs = new URLSearchParams();
  if (date) qs.set("date", date);
  if (email) qs.set("email", email);
  const query = qs.toString();
  return api(`/admin/activity${query ? `?${query}` : ""}`, "GET");
};
export const fetchAdminDashboard = () => api("/admin/dashboard", "GET");

/* ================= PROJECTS & TASKS ================= */

export const fetchProjects = (params = {}) => {
  const qs = new URLSearchParams();
  if (params.status) qs.set("status", params.status);
  const query = qs.toString();
  return api(`/projects${query ? `?${query}` : ""}`, "GET");
};

/* ================= SHIFTS ================= */

export const fetchDepartments = () => api("/departments", "GET");
export const fetchAdminDepartments = () => api("/admin/departments", "GET");
export const createDepartment = (data) => api("/admin/departments", "POST", data);
export const updateDepartment = (departmentId, data) =>
  api(`/admin/departments/${encodeURIComponent(departmentId)}`, "PATCH", data);
export const setDepartmentStatus = (departmentId, status) =>
  api(`/admin/departments/${encodeURIComponent(departmentId)}/status`, "POST", {
    status,
  });
export const fetchDepartmentDesignations = (departmentId) =>
  api(`/departments/${encodeURIComponent(departmentId)}/designations`, "GET");
export const fetchAdminDepartmentDesignations = (departmentId) =>
  api(
    `/admin/departments/${encodeURIComponent(departmentId)}/designations`,
    "GET"
  );
export const createDepartmentDesignation = (departmentId, data) =>
  api(
    `/admin/departments/${encodeURIComponent(departmentId)}/designations`,
    "POST",
    data
  );
export const updateDepartmentDesignation = (departmentId, designationId, data) =>
  api(
    `/admin/departments/${encodeURIComponent(departmentId)}/designations/${encodeURIComponent(designationId)}`,
    "PATCH",
    data
  );
export const setDepartmentDesignationStatus = (departmentId, designationId, status) =>
  api(
    `/admin/departments/${encodeURIComponent(departmentId)}/designations/${encodeURIComponent(designationId)}/status`,
    "POST",
    { status }
  );
export const fetchEmployeeDepartments = (email) =>
  api(`/admin/employees/${encodeURIComponent(email)}/departments`, "GET");
export const saveEmployeeDepartments = (email, data) =>
  api(`/admin/employees/${encodeURIComponent(email)}/departments`, "PUT", data);

export const fetchShifts = () => api("/shifts", "GET");
export const createShift = (data) => api("/shifts", "POST", data);
export const updateShift = (shiftId, data) =>
  api(`/shifts/${encodeURIComponent(shiftId)}`, "PATCH", data);
export const fetchEmployeeShift = (email) =>
  api(`/employees/${encodeURIComponent(email)}/shift`, "GET");
export const assignEmployeeShift = (email, data) =>
  api(`/employees/${encodeURIComponent(email)}/shift`, "PUT", data);

export const fetchCostRateDesignations = () =>
  api("/admin/cost-rates/designations", "GET");
export const createCostRateDesignation = (data) =>
  api("/admin/cost-rates/designations", "POST", data);
export const updateCostRateDesignation = (id, data) =>
  api(`/admin/cost-rates/designations/${encodeURIComponent(id)}`, "PATCH", data);
export const setCostRateDesignationStatus = (id, status) =>
  api(
    `/admin/cost-rates/designations/${encodeURIComponent(id)}/status`,
    "POST",
    { status }
  );
export const fetchCostRateEmployees = () =>
  api("/admin/cost-rates/employees", "GET");
export const fetchCostRateEmployee = (email) =>
  api(`/admin/cost-rates/employees/${encodeURIComponent(email)}`, "GET");
export const saveCostRateEmployee = (email, data) =>
  api(`/admin/cost-rates/employees/${encodeURIComponent(email)}`, "PUT", data);
export const setCostRateEmployeeStatus = (email, status) =>
  api(
    `/admin/cost-rates/employees/${encodeURIComponent(email)}/status`,
    "POST",
    { status }
  );

export const fetchProjectCosting = (projectId, params = {}) => {
  const qs = new URLSearchParams();
  if (params.limit) qs.set("limit", String(params.limit));
  if (params.cursor) qs.set("cursor", params.cursor);
  const query = qs.toString();
  return api(
    `/admin/costing/projects/${encodeURIComponent(projectId)}${
      query ? `?${query}` : ""
    }`,
    "GET"
  );
};

export const fetchCostingRoster = (params = {}) => {
  const qs = new URLSearchParams();
  if (params.from) qs.set("from", params.from);
  if (params.to) qs.set("to", params.to);
  const query = qs.toString();
  return api(`/admin/costing/employees${query ? `?${query}` : ""}`, "GET");
};

export const fetchEmployeeCosting = (email, params = {}) => {
  const qs = new URLSearchParams();
  if (params.from) qs.set("from", params.from);
  if (params.to) qs.set("to", params.to);
  if (params.limit) qs.set("limit", String(params.limit));
  if (params.cursor) qs.set("cursor", params.cursor);
  const query = qs.toString();
  return api(
    `/admin/costing/employees/${encodeURIComponent(email)}${
      query ? `?${query}` : ""
    }`,
    "GET"
  );
};

export const createProject = (data) => api("/projects", "POST", data);
export const updateProject = (projectId, data) =>
  api(`/projects/${encodeURIComponent(projectId)}`, "PATCH", data);
export const deleteProject = (projectId, confirmName) =>
  api(`/projects/${encodeURIComponent(projectId)}`, "DELETE", {
    confirmName: String(confirmName ?? ""),
  });
export const fetchTasks = async (params = {}) => {
  const data = await fetchTaskList(params);
  return data.tasks;
};

export const fetchTaskList = async (params = {}) => {
  const clean = {};
  Object.entries(params || {}).forEach(([key, value]) => {
    if (value === undefined || value === null || value === "") return;
    clean[key] = value;
  });
  const qs = new URLSearchParams(clean).toString();
  const data = await api(`/tasks${qs ? `?${qs}` : ""}`, "GET");
  if (Array.isArray(data)) {
    return { tasks: data, zoneCounts: null };
  }
  return {
    tasks: Array.isArray(data?.tasks) ? data.tasks : [],
    zoneCounts: data?.zoneCounts || null,
  };
};
export const createTask = (data) => api("/tasks", "POST", data);
export const getTaskImportUploadUrl = ({ fileName, contentType, fileSize }) =>
  api("/task-imports/upload-url", "POST", { fileName, contentType, fileSize });

export const previewTaskImport = (batchId) =>
  api(`/task-imports/${encodeURIComponent(batchId)}/preview`, "POST");

export const confirmTaskImport = (batchId) =>
  api(`/task-imports/${encodeURIComponent(batchId)}/confirm`, "POST");
export const fetchTaskImports = (params = {}) => {
  const qs = new URLSearchParams();
  if (params.limit) qs.set("limit", String(params.limit));
  if (params.nextToken) qs.set("nextToken", params.nextToken);
  const query = qs.toString();
  return api(`/task-imports${query ? `?${query}` : ""}`, "GET");
};
export const fetchTaskImport = (batchId) =>
  api(`/task-imports/${encodeURIComponent(batchId)}`, "GET");
export const getTaskImportDownloadUrl = (batchId) =>
  api(`/task-imports/${encodeURIComponent(batchId)}/download-url`, "GET");

export const updateTask = (data) => api("/tasks", "PUT", data);
export const archiveTask = (taskId) =>
  api("/tasks", "DELETE", { taskId });

export const fetchTaskById = async (taskId) => {
  try {
    return await api(`/tasks/${encodeURIComponent(taskId)}`, "GET");
  } catch (err) {
    if (err?.status === 401 || err?.status === 403) throw err;
    // Older/live API may not expose GET /tasks/{id} yet — fall back to own tasks.
    const list = await fetchTasks({ mine: "true" });
    const found = (Array.isArray(list) ? list : []).find(
      (t) => String(t.taskId) === String(taskId)
    );
    if (!found) throw err;
    return found;
  }
};

export const fetchTaskComments = (taskId) =>
  api(`/tasks/${encodeURIComponent(taskId)}/comments`, "GET");
export const postTaskComment = (taskId, text) =>
  api(`/tasks/${encodeURIComponent(taskId)}/comments`, "POST", { text });
export const fetchTaskActivity = (taskId) =>
  api(`/tasks/${encodeURIComponent(taskId)}/activity`, "GET");
export const reportTaskBlocker = (taskId, remark) =>
  api(`/tasks/${encodeURIComponent(taskId)}/blocker`, "POST", { remark });
export const resolveTaskBlocker = (taskId, assignmentEmail) =>
  api(`/tasks/${encodeURIComponent(taskId)}/blocker/resolve`, "POST", {
    assignmentEmail,
  });
export const fetchTaskAttachments = (taskId) =>
  api(`/tasks/${encodeURIComponent(taskId)}/attachments`, "GET");
export const getTaskAttachmentUploadUrl = (taskId, fileName, contentType, fileSize) =>
  api(`/tasks/${encodeURIComponent(taskId)}/attachment-upload-url`, "POST", {
    fileName,
    contentType,
    fileSize,
  });
export const registerTaskAttachment = (taskId, payload) =>
  api(`/tasks/${encodeURIComponent(taskId)}/attachments`, "POST", payload);
export const getTaskAttachmentDownloadUrl = (taskId, payload) =>
  api(
    `/tasks/${encodeURIComponent(taskId)}/attachment-download-url`,
    "POST",
    payload
  );
export const logTimeEntry = (data) => api("/time-entries", "POST", data);
export const fetchTimeEntries = (email) =>
  api(`/time-entries${email ? `?email=${encodeURIComponent(email)}` : ""}`, "GET");
export const saveTaskPlannedHours = (taskId, hours) =>
  api(`/tasks/${encodeURIComponent(taskId)}/planned-hours`, "PUT", { hours });

/* ================= LEAVE ================= */

export const fetchMyLeave = () => api("/leave", "GET");
export const fetchWeekOffState = ({ weekStart, email } = {}) => {
  const qs = new URLSearchParams({ weekOff: "true" });
  if (weekStart) qs.set("weekStart", weekStart);
  if (email) qs.set("email", email);
  return api(`/leave?${qs.toString()}`, "GET");
};
export const fetchAllLeave = () => api("/leave?all=true", "GET");
export const fetchLeaveNotifications = () =>
  api("/leave?notifications=true", "GET");
export const markNotificationRead = (sk) =>
  api("/leave", "PUT", { action: "readNotification", sk });
export const markAllNotificationsRead = () =>
  api("/leave", "PUT", { action: "readAllNotifications" });
export const applyLeave = (data) => api("/leave", "POST", data);
export const reviewLeave = (leaveId, status, rejectionReason) =>
  api("/leave", "PUT", { leaveId, status, rejectionReason });
export const cancelLeave = (leaveId) =>
  api("/leave", "PUT", { leaveId, status: "CANCELLED" });

/* ================= RESIGNATION ================= */

function asResignationList(value, email, name) {
  const rows = Array.isArray(value) ? value : [];
  return rows
    .map((row) => {
      if (!row || typeof row !== "object") return null;
      const id =
        row.resignationId ||
        String(row.SK || "").replace(/^RESIGNATION#/i, "") ||
        "";
      if (!id && !row.createdAt) return null;
      return {
        resignationId: id || row.createdAt,
        email: String(row.email || email || "").toLowerCase(),
        name: row.name || name || "",
        lastWorkingDay: row.lastWorkingDay || "",
        reason: row.reason || "",
        status: row.status || "SUBMITTED",
        createdAt: row.createdAt || null,
        reviewedAt: row.reviewedAt || null,
        reviewedBy: row.reviewedBy || null,
      };
    })
    .filter(Boolean);
}

function mergeResignations(...lists) {
  const byId = new Map();
  lists.flat().forEach((row) => {
    if (!row) return;
    const key = String(row.resignationId || `${row.email}-${row.createdAt}`);
    const prev = byId.get(key);
    if (!prev) {
      byId.set(key, row);
      return;
    }
    const prevTime = String(prev.reviewedAt || prev.createdAt || "");
    const nextTime = String(row.reviewedAt || row.createdAt || "");
    if (nextTime.localeCompare(prevTime) >= 0) byId.set(key, { ...prev, ...row });
  });
  return Array.from(byId.values()).sort((a, b) =>
    String(b.createdAt || "").localeCompare(String(a.createdAt || ""))
  );
}

function profileWithoutMeta(profile) {
  const next = { ...(profile || {}) };
  delete next.PK;
  delete next.SK;
  delete next.profileImageUrl;
  delete next.imageStorageUrl;
  return next;
}

async function resignationsFromProfile(email) {
  const me = currentEmail();
  const profile = await fetchUserProfile(
    email && email !== me ? email : undefined
  );
  return asResignationList(
    profile?.resignations,
    profile?.email || email || me,
    profile?.name
  );
}

async function saveResignationsOnProfile(email, resignations, extraProfile = {}) {
  const me = currentEmail();
  const profile = await fetchUserProfile(
    email && email !== me ? email : undefined
  );
  await saveUserProfile({
    mode: "EDIT",
    email,
    profile: {
      ...profileWithoutMeta(profile),
      ...extraProfile,
      email,
      resignations,
    },
  });
  return resignations;
}

export const fetchMyResignations = async () => {
  const email = currentEmail();
  let fromApi = [];
  try {
    const rows = await apiOptional("/resignation", "GET");
    if (Array.isArray(rows)) fromApi = asResignationList(rows, email);
  } catch {
    fromApi = [];
  }
  let fromProfile = [];
  try {
    fromProfile = await resignationsFromProfile(email);
  } catch {
    fromProfile = [];
  }
  return mergeResignations(fromApi, fromProfile);
};

export const fetchAllResignations = async () => {
  let fromApi = [];
  try {
    const rows = await apiOptional("/resignation?all=true", "GET");
    if (Array.isArray(rows)) fromApi = asResignationList(rows);
  } catch {
    fromApi = [];
  }

  let fromProfiles = [];
  try {
    const users = await fetchUsers();
    const list = Array.isArray(users) ? users : [];
    const nested = await Promise.all(
      list.map(async (u) => {
        const email = String(u.email || "").toLowerCase();
        if (!email) return [];
        try {
          const profile = await fetchUserProfile(email);
          return asResignationList(
            profile?.resignations,
            email,
            profile?.name || u.name
          );
        } catch {
          return [];
        }
      })
    );
    fromProfiles = nested.flat();
  } catch {
    fromProfiles = [];
  }

  return mergeResignations(fromApi, fromProfiles);
};

export const submitResignation = async (data) => {
  const email = currentEmail();
  const lastWorkingDay = String(data?.lastWorkingDay || "").trim();
  const reason = String(data?.reason || "").trim();
  if (!lastWorkingDay || !reason) {
    throw new Error("lastWorkingDay and reason are required");
  }

  let apiRow = null;
  try {
    const res = await apiOptional("/resignation", "POST", {
      lastWorkingDay,
      reason,
    });
    apiRow = res?.resignation || null;
  } catch {
    apiRow = null;
  }

  const profile = await fetchUserProfile().catch(() => ({}));
  const existing = asResignationList(profile?.resignations, email, profile?.name);
  const item = {
    resignationId:
      apiRow?.resignationId ||
      `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    email,
    name: profile?.name || email.split("@")[0],
    lastWorkingDay,
    reason,
    status: "SUBMITTED",
    createdAt: new Date().toISOString(),
  };
  await saveResignationsOnProfile(email, [item, ...existing], {
    name: profile?.name || "",
  });
  return item;
};

export const reviewResignation = async (resignationId, status, email) => {
  const nextStatus = String(status || "").toUpperCase();
  try {
    await apiOptional("/resignation", "PUT", {
      resignationId,
      status: nextStatus,
      email,
    });
  } catch {
    /* live API may not support PUT yet */
  }

  const target = String(email || "").toLowerCase();
  if (!target) return;
  const profile = await fetchUserProfile(target);
  const existing = asResignationList(
    profile?.resignations,
    target,
    profile?.name
  );
  const reviewedAt = new Date().toISOString();
  const reviewedBy = currentEmail();
  const next = existing.map((row) =>
    row.resignationId === resignationId
      ? { ...row, status: nextStatus, reviewedAt, reviewedBy }
      : row
  );
  await saveResignationsOnProfile(target, next, { name: profile?.name || "" });
};

/* ================= ANNOUNCEMENTS ================= */

function filterPortalAnnouncements(items) {
  return (Array.isArray(items) ? items : []).filter(
    (a) => !isMeetingAnnouncement(a)
  );
}

export const fetchAnnouncements = async () => {
  const items = await api("/announcements", "GET");
  return filterPortalAnnouncements(items);
};

/**
 * Admin history. Uses apiOptional because GET /admin/announcements is a
 * newer method: missing-route or "Admin required" 403s should not fail
 * the employee announcement feed.
 */
export const fetchAnnouncementHistory = async () => {
  try {
    const items = await apiOptional("/admin/announcements", "GET");
    return filterPortalAnnouncements(items);
  } catch {
    return fetchAnnouncements();
  }
};
export const createAnnouncement = (data) =>
  api("/admin/announcements", "POST", data);
export const updateAnnouncement = (data) =>
  apiOptional("/admin/announcements", "PUT", data);
export const deleteAnnouncement = (announceId) =>
  api("/admin/announcements", "DELETE", { announceId });

/* ================= MEETINGS ================= */

function currentEmail() {
  const token = localStorage.getItem("token");
  const payload = parseJwtPayload(token);
  return String(
    payload?.email || payload?.["cognito:username"] || payload?.username || ""
  ).toLowerCase();
}

async function listStoredMeetings() {
  const items = await api("/announcements", "GET");
  return (Array.isArray(items) ? items : [])
    .map(parseMeetingAnnouncement)
    .filter(Boolean)
    .sort((a, b) =>
      String(a.startDateTime || "").localeCompare(String(b.startDateTime || ""))
    );
}

async function findStoredMeeting(meetingId) {
  const rows = await listStoredMeetings();
  return rows.find((m) => m.meetingId === meetingId) || null;
}

function buildMeetingRecord(data, existing) {
  const date = toIsoDate(data.date);
  const startTime = padTime(data.startTime);
  const endTime = padTime(data.endTime) || "";
  const startDateTime = combineIstIso(date, startTime);
  const endDateTime = combineIstIso(date, endTime);
  const names = data.participantNames || {};
  const emails = (data.participantEmails || []).map((e) =>
    String(e || "").trim().toLowerCase()
  );
  const prevByEmail = new Map(
    (existing?.participants || []).map((p) => [
      String(p.employeeId || "").toLowerCase(),
      p,
    ])
  );
  const now = new Date().toISOString();
  const participants = emails.map((email) => {
    const prev = prevByEmail.get(email);
    return {
      employeeId: email,
      employeeName: prev?.employeeName || names[email] || displayNameFromEmail(email),
      invitedAt: prev?.invitedAt || now,
      joinTime: prev?.joinTime || null,
      leaveTime: prev?.leaveTime || null,
      attendanceStatus: prev?.attendanceStatus || "INVITED",
    };
  });
  return withComputedMeeting({
    meetingId: existing?.meetingId || (crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}`),
    title: String(data.title || "").trim(),
    description: String(data.description || "").trim(),
    meetingType: data.meetingType || "ZOOM",
    meetingLink: String(data.meetingLink || "").trim(),
    date,
    startTime,
    endTime,
    startDateTime,
    endDateTime,
    createdBy: existing?.createdBy || currentEmail(),
    createdAt: existing?.createdAt || now,
    updatedAt: now,
    status: existing?.status === "CANCELLED" ? "CANCELLED" : "SCHEDULED",
    cancelledAt: existing?.cancelledAt || null,
    participants,
    participantCount: participants.length,
  });
}

async function saveStoredMeeting(meeting, previousAnnounceId) {
  const created = await createAnnouncement(serializeMeetingAnnouncement(meeting));
  if (previousAnnounceId) {
    try {
      await deleteAnnouncement(previousAnnounceId);
    } catch {
      /* keep the new copy even if old delete fails */
    }
  }
  return parseMeetingAnnouncement(created) || withComputedMeeting({
    ...meeting,
    announceId: created?.announceId,
  });
}

export const fetchMyMeetings = async () => {
  const email = currentEmail();
  const rows = await listStoredMeetings();
  return rows.filter((m) =>
    (m.participants || []).some(
      (p) => String(p.employeeId || "").toLowerCase() === email
    )
  );
};

export const fetchMyMeeting = async (meetingId) => {
  const rows = await fetchMyMeetings();
  const meeting = rows.find((m) => m.meetingId === meetingId);
  if (!meeting) throw new Error("Meeting not found.");
  return meeting;
};

export const fetchAdminMeetings = () => listStoredMeetings();

export const fetchAdminMeeting = async (meetingId) => {
  const meeting = await findStoredMeeting(meetingId);
  if (!meeting) throw new Error("Meeting not found.");
  return meeting;
};

export const createMeeting = async (data) => {
  const meeting = buildMeetingRecord(data);
  if (!meeting.date) throw new Error("Date is required.");
  if (!meeting.startTime) throw new Error("Start time is required.");
  if (!meeting.endTime) throw new Error("Start time is required.");
  return saveStoredMeeting(meeting);
};

export const updateMeeting = async (data) => {
  const existing = await findStoredMeeting(data.meetingId);
  if (!existing) throw new Error("Meeting not found.");
  const meeting = buildMeetingRecord(data, existing);
  return saveStoredMeeting(meeting, existing.announceId);
};

export const cancelMeeting = async (meetingId) => {
  const existing = await findStoredMeeting(meetingId);
  if (!existing) throw new Error("Meeting not found.");
  const now = new Date().toISOString();
  const meeting = withComputedMeeting({
    ...existing,
    status: "CANCELLED",
    cancelledAt: now,
    updatedAt: now,
  });
  return saveStoredMeeting(meeting, existing.announceId);
};

export const joinMeeting = async (meetingId) => {
  const meeting = await fetchMyMeeting(meetingId);
  if (meeting.status === "CANCELLED") {
    throw new Error("This meeting has been cancelled.");
  }
  if (meeting.status === "COMPLETED") {
    throw new Error("This meeting has already ended.");
  }
  if (!meeting.meetingLink) {
    throw new Error("Meeting link is not available.");
  }
  return {
    ok: true,
    meetingLink: meeting.meetingLink,
    joinTime: new Date().toISOString(),
    attendanceStatus: "JOINED",
  };
};

/* ================= CONSENT & DOWNLOADS ================= */

export const fetchConsent = () => api("/consent", "GET");
export const acceptConsent = () => api("/consent", "POST", {});
/* ================= SOFTWARE CENTER ================= */

export const fetchSoftware = () => api("/software", "GET");
export const fetchAdminSoftware = () => api("/admin/software", "GET");
export const createSoftware = (data) => api("/admin/software", "POST", data);
export const updateSoftware = (data) => api("/admin/software", "PUT", data);
export const deleteSoftware = (softwareId) =>
  api("/admin/software", "DELETE", { softwareId });
export const getSoftwareUploadUrl = (fileName, softwareId) =>
  api("/admin/software/upload-url", "POST", { fileName, softwareId });

/* ================= ATTENDANCE ================= */

export const fetchAttendance = (startDate, endDate, email) => {
  const qs = new URLSearchParams({ startDate, endDate });
  if (email) qs.set("email", email);
  return api(`/attendance?${qs.toString()}`, "GET");
};
export const saveAttendance = (records) =>
  api("/attendance", "POST", records);

/** Server-authoritative check-in; client timestamps are ignored. */
export const attendanceCheckIn = ({ date } = {}) =>
  api("/attendance", "POST", { action: "checkIn", date });

/** Server-authoritative check-out; client timestamps are ignored. */
export const attendanceCheckOut = ({ date, workedBeyondReason } = {}) =>
  api("/attendance", "POST", {
    action: "checkOut",
    date,
    ...(workedBeyondReason ? { workedBeyondReason } : {}),
  });

/** Admin: all employees' check-in / check-out activity */
export const fetchAttendanceActivity = (params = {}) => {
  const qs = new URLSearchParams();
  if (params.date) qs.set("date", params.date);
  if (params.status) qs.set("status", params.status);
  if (params.search) qs.set("search", params.search);
  if (params.page) qs.set("page", String(params.page));
  if (params.pageSize) qs.set("pageSize", String(params.pageSize));
  const query = qs.toString();
  return api(`/admin/attendance-activity${query ? `?${query}` : ""}`, "GET");
};

/* ================= DOCUMENTS ================= */

export const fetchMyDocuments = () => api("/documents", "GET");
export const fetchAllDocuments = () => api("/admin/documents", "GET");
export const fetchEmployeeDocuments = (email) =>
  api(`/documents?email=${encodeURIComponent(email)}`, "GET");
export const fetchDocumentTypes = () => api("/documents/types", "GET");
export const updateDocumentTypes = (types) =>
  api("/documents/types", "PUT", { types });
export const getDocumentUploadUrl = (payload) =>
  api("/documents/upload-url", "POST", payload);
export const registerDocument = (payload) => api("/documents", "POST", payload);
export const getDocumentViewUrl = (documentId, disposition) =>
  api("/documents/view-url", "POST", { documentId, disposition });
export const reviewDocument = (documentId, status, rejectionReason) =>
  api("/documents", "PUT", { documentId, status, rejectionReason });
export const fetchDocumentHistory = (documentType, email) => {
  const qs = new URLSearchParams({ history: "true", documentType });
  if (email) qs.set("email", email);
  return api(`/documents?${qs.toString()}`, "GET");
};

function projectFolderPath(projectId, folderId, suffix = "") {
  const base =
    !folderId || folderId === "root"
      ? `/documents/projects/${encodeURIComponent(projectId)}`
      : `/documents/projects/${encodeURIComponent(projectId)}/folders/${encodeURIComponent(folderId)}`;
  return `${base}${suffix}`;
}

export const fetchDocumentProjects = () => api("/documents/projects", "GET");
export const createDocumentProject = (name) =>
  api("/documents/projects", "POST", { name });
export const renameDocumentProject = (projectId, name) =>
  api(`/documents/projects/${encodeURIComponent(projectId)}`, "PATCH", { name });
export const deleteDocumentProject = (projectId) =>
  api(`/documents/projects/${encodeURIComponent(projectId)}`, "DELETE");
export const fetchDocumentProjectFolder = (projectId, folderId) =>
  api(projectFolderPath(projectId, folderId, folderId && folderId !== "root" ? "" : "/folders"), "GET");
export const createDocumentProjectSubfolder = (projectId, folderId, name) =>
  api(projectFolderPath(projectId, folderId, "/subfolders"), "POST", { name });
export const getDocumentProjectUploadUrl = (projectId, folderId, payload) =>
  api(projectFolderPath(projectId, folderId, "/files"), "POST", {
    action: "upload-url",
    ...payload,
  });
export const uploadDocumentProjectFiles = (projectId, folderId, payload) =>
  api(projectFolderPath(projectId, folderId, "/files"), "POST", payload);
export const renameDocumentProjectFolder = (projectId, folderId, name) =>
  api(
    `/documents/projects/${encodeURIComponent(projectId)}/folders/${encodeURIComponent(folderId)}`,
    "PATCH",
    { name }
  );
export const deleteDocumentProjectFolder = (projectId, folderId) =>
  api(
    `/documents/projects/${encodeURIComponent(projectId)}/folders/${encodeURIComponent(folderId)}`,
    "DELETE"
  );
export const renameDocumentProjectFile = (projectId, folderId, fileId, name) =>
  api(`${projectFolderPath(projectId, folderId)}/files/${encodeURIComponent(fileId)}`, "PATCH", {
    name,
  });
export const deleteDocumentProjectFile = (projectId, folderId, fileId) =>
  api(`${projectFolderPath(projectId, folderId)}/files/${encodeURIComponent(fileId)}`, "DELETE");
export const getDocumentProjectDownloadUrl = (projectId, folderId, fileId) =>
  api(
    `${projectFolderPath(projectId, folderId)}/files/${encodeURIComponent(fileId)}/download-url`,
    "POST",
    {}
  );

function personalFolderPath(email, folderId, suffix = "") {
  const owner = String(email || "").trim().toLowerCase();
  const base = owner
    ? `/documents/personal/${encodeURIComponent(owner)}`
    : `/documents/personal`;
  if (!folderId || folderId === "root") return `${base}${suffix}`;
  return `${base}/folders/${encodeURIComponent(folderId)}${suffix}`;
}

export const fetchDocumentPersonalFolder = (email, folderId) =>
  api(personalFolderPath(email, folderId), "GET");
export const createDocumentPersonalSubfolder = (email, folderId, name) =>
  api(personalFolderPath(email, folderId, "/subfolders"), "POST", { name });
export const getDocumentPersonalUploadUrl = (email, folderId, payload) =>
  api(personalFolderPath(email, folderId, "/files"), "POST", {
    action: "upload-url",
    ...payload,
  });
export const uploadDocumentPersonalFiles = (email, folderId, payload) =>
  api(personalFolderPath(email, folderId, "/files"), "POST", payload);
export const renameDocumentPersonalFolder = (email, folderId, name) =>
  api(
    `/documents/personal/${encodeURIComponent(email)}/folders/${encodeURIComponent(folderId)}`,
    "PATCH",
    { name }
  );
export const deleteDocumentPersonalFolder = (email, folderId) =>
  api(
    `/documents/personal/${encodeURIComponent(email)}/folders/${encodeURIComponent(folderId)}`,
    "DELETE"
  );
export const renameDocumentPersonalFile = (email, folderId, fileId, name) =>
  api(`${personalFolderPath(email, folderId)}/files/${encodeURIComponent(fileId)}`, "PATCH", {
    name,
  });
export const deleteDocumentPersonalFile = (email, folderId, fileId) =>
  api(`${personalFolderPath(email, folderId)}/files/${encodeURIComponent(fileId)}`, "DELETE");
export const getDocumentPersonalDownloadUrl = (email, folderId, fileId) =>
  api(
    `${personalFolderPath(email, folderId)}/files/${encodeURIComponent(fileId)}/download-url`,
    "POST",
    {}
  );

export async function putToSignedUrl(uploadUrl, file, contentType) {
  const headers = {};
  if (contentType) headers["Content-Type"] = contentType;
  let res;
  try {
    res = await fetch(uploadUrl, { method: "PUT", headers, body: file });
  } catch (networkErr) {
    const err = new Error(
      "Unable to reach the server. Check your connection and try again."
    );
    err.cause = networkErr;
    err.isNetworkError = true;
    throw err;
  }
  if (!res.ok) {
    throw new Error("File upload failed. Please try again.");
  }
}

export const fetchDocumentNotificationFeed = () =>
  apiOptional("/documents/notifications/feed", "GET");
export const markDocumentNotificationsSeen = (lastSeenAt) =>
  apiOptional("/documents/notifications/mark-seen", "POST", {
    lastSeenAt: lastSeenAt || new Date().toISOString(),
  });
